import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import ProductsMarketFilter from '@components/products-line-charts/products-market-filter/ProductsMarketFilter';
import FeatureSelector from '@components/feature-selector/FeatureSelector';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import { useInViewTrackingForPerformanceAnalysis } from '@components/performance-analysis-card/PerformanceAnalysisCardHooks';
import { PerformanceAnalysisView } from '@components/performance-analysis-card/Types';
import { renderLoading } from '@pages/Loading';
import { PERFORMANCE_ANALYSIS_TREND_CARD_TITLE } from '@pages/Utilities';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { FactRevenue, FeatureOption, getPageTypeWithPageName, ShareType, SubSection } from '@utils/pageConfig/Types';
import { getSaleTypeTrackingValue, RetailerKpiOption, SaleType } from '@models/Products';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';
import { getHeaderKey, transformShareTypeToRetailerKpiOption } from '@data-visualization/utilities/ChartUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  DefaultSortingOptionsForPerformanceAnalysisboard,
  PerformanceAnalysisboardTableId,
} from '@models/PerformanceAnalysisboard';
import { ExportPerformanceBySegmentResp } from '@data-visualization/performance-analysis-line-chart/Types';
import { ExportExploreTrendsStack } from '@data-visualization/trends-stack-chart/Types';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { SortFieldDisplayProps } from '@components/design/migrated/Table';

interface CombinedMultiChartToggleView<T> {
  data: ExportPerformanceBySegmentResp | ExportExploreTrendsStack;
  transformData: (data: ExportPerformanceBySegmentResp | ExportExploreTrendsStack) => any;
  loading: boolean;
  selectedGrowthType?: GrowthTypes;
  selectedPeriodicity: Periodicity;
  factRevenue: FactRevenue;
  selectedShareType: ShareType;
  onChangeSelectedShareType: (share: ShareType) => void;
  featureSelectorType: FeatureSelectorType;
  featureHandler: () => void;
  noResultHandler: () => void;
  volume: string;
  selectedRetailerOption: RetailerKpiOption;
  setSelectedRetailerOption: Dispatch<SetStateAction<RetailerKpiOption>>;
  saleType: SaleType;
  setSaleType: Dispatch<SetStateAction<SaleType>>;
  noPeriodExistForMulticountry?: boolean;
  isExplorePage?: boolean;
  trackingTitle: string;
  CardMarket: React.ComponentType<any>;
  CardRetailer: React.ComponentType<any>;
  marketSaleTypeProp: string;
  retailerSaleTypeProp: string;
  absoluteType?: any;
  stackedChartSortingOrder?: SortFieldDisplayProps;
  subSectionForAmplitudeTracking: SubSection;
}

const CombinedMultiChartView = <T,>({
  data,
  transformData,
  loading,
  selectedGrowthType,
  selectedPeriodicity,
  factRevenue,
  selectedShareType,
  onChangeSelectedShareType,
  featureSelectorType,
  featureHandler,
  noResultHandler,
  volume,
  selectedRetailerOption,
  setSelectedRetailerOption,
  saleType,
  setSaleType,
  noPeriodExistForMulticountry,
  isExplorePage,
  trackingTitle,
  CardMarket,
  CardRetailer,
  marketSaleTypeProp,
  retailerSaleTypeProp,
  absoluteType,
  stackedChartSortingOrder,
  subSectionForAmplitudeTracking,
}: CombinedMultiChartToggleView<T>) => {
  // Persisted sorting state
  const { sorting, updateOptions } = usePersistedSorting({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });

  const modifiedData = transformData(data);

  const absoluteTypeProp = absoluteType ? { selectedAbsoluteType: absoluteType } : {};

  useEffect(() => {
    setSelectedRetailerOption(transformShareTypeToRetailerKpiOption(selectedShareType));
  }, [selectedShareType, setSelectedRetailerOption]);

  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();

  const handleChartInteractionTracking = (saleTypeValue: string) => {
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: ChartElementName.SHOW,
      elementType: ChartElementType.FILTER,
      optionChosen: saleTypeValue,
      chartName: `${trackingTitle} Market Trend`,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
  };

  const hasData: boolean = data.salesRecordsFound;

  // Set up the in-view tracking refs for performance measurement
  const { ref: refMarketCard } = useInViewTrackingForPerformanceAnalysis(
    PerformanceAnalysisView.LineChart,
    getSaleTypeTrackingValue(volume)[saleType],
    hasData,
    `${trackingTitle} - Market`,
    subSectionForAmplitudeTracking,
  );

  const { ref: refForRetailerCard } = useInViewTrackingForPerformanceAnalysis(
    PerformanceAnalysisView.LineChart,
    getSaleTypeTrackingValue(volume)[saleType],
    hasData,
    `${trackingTitle} - Retailer`,
    subSectionForAmplitudeTracking,
  );

  const handleOnChange = (value: string) => {
    const saleTypeTrackingValue = getSaleTypeTrackingValue(volume)[value as SaleType];
    handleChartInteractionTracking(saleTypeTrackingValue);

    const newSaleType = SaleType[value as keyof typeof SaleType];
    updateOptions({
      ...sorting,
      headerKey: getHeaderKey(newSaleType),
    });
    setSaleType(newSaleType);
    featureHandler();
  };

  return (
    <>
      {!isExplorePage && (
        <div className="flex-row flex-wrap gap-4">
          <div>
            <ProductsMarketFilter
              selectedLineChartSaleType={saleType}
              setSelectedLineChartSaleType={setSaleType}
              changeHandler={handleOnChange}
              factRevenue={factRevenue}
              volume={volume}
              retailerClientStateKey={'retailerClient'}
            />
          </div>
          <div
            style={{ width: '400px' }}
            key={`${featureSelectorType.isDisabled}-${featureSelectorType.featureFilters}`} //NOSONAR typescript:S6551
          >
            <FeatureSelector
              isDisabled={featureSelectorType.isDisabled}
              label={'Show performance within'}
              featureValues={featureSelectorType.featureFilters}
              featureOptions={(feat: FeatureOption) => featureSelectorType.setFeature(feat)}
              selectedOption={featureSelectorType.feature}
              withoutDiv={true}
              featureOnChange={featureHandler}
              chartName={`${PERFORMANCE_ANALYSIS_TREND_CARD_TITLE} ${ElementType.LINE_CHART}`}
            />
          </div>
        </div>
      )}
      <div>
        {loading ? (
          renderLoading()
        ) : (
          <>
            <div ref={refMarketCard}>
              <CardMarket
                data={modifiedData}
                selectedPeriodicity={selectedPeriodicity}
                selectedGrowthType={selectedGrowthType}
                currencySymbol={factRevenue.currencySymbol}
                {...{ [marketSaleTypeProp]: saleType }}
                {...absoluteTypeProp}
                noResultHandler={noResultHandler}
                noPeriodExistForMulticountry={noPeriodExistForMulticountry}
                stackedChartSortingOrder={stackedChartSortingOrder}
              />
            </div>
            <div ref={refForRetailerCard} className="mt-8">
              <CardRetailer
                data={modifiedData}
                selectedPeriodicity={selectedPeriodicity}
                selectedGrowthType={selectedGrowthType}
                currencySymbol={factRevenue.currencySymbol}
                {...{ [retailerSaleTypeProp]: saleType }}
                {...absoluteTypeProp}
                selectedRetailerOption={selectedRetailerOption}
                setSelectedRetailerOption={setSelectedRetailerOption}
                onChangeSelectedRetailerOption={onChangeSelectedShareType}
                noResultHandler={noResultHandler}
                noPeriodExistForMulticountry={noPeriodExistForMulticountry}
                isExplorePage={isExplorePage}
                stackedChartSortingOrder={stackedChartSortingOrder}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CombinedMultiChartView;
